import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { TripSummaryService } from '@common/services/my-booking/trip-summary.service';
import { SupportComponent } from '@lib/components/support-class';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { Bound, GetOrderResponseData, Type1 } from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

@Component({
  selector: 'asw-flight-item',
  templateUrl: './flight-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightItemComponent extends SupportComponent {
  id = this.constructor.name;
  @Input() boundIdx!: number;
  @Input() flightIdx!: number;
  @Input() isFirstFlight!: boolean;
  @Input() isLastFlight!: boolean;

  flight: Type1 = {};
  bound: Bound = {};
  totalFlight: number = 0;
  isEarlyDeparture: boolean = false;

  private _subscriptions = new Subscription();

  /** 定数用 */
  public appConstants = AppConstants;

  constructor(
    public tripSummarySvc: TripSummaryService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _change: ChangeDetectorRef,
    _common: CommonLibService
  ) {
    super(_common);
  }

  override init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this.bound = pnr.air?.bounds?.at(this.boundIdx) ?? {};
        this.flight = this.bound.flights?.at(this.flightIdx) ?? {};
        this.totalFlight = this.bound.flights?.length ?? 0;
        this.isEarlyDeparture = this.bound.earlyDeparture?.isContainedSegment ?? false;
        this._change.markForCheck();
      })
    );
  }

  // (1.1.18) 出発地までの経路案内
  isRouteDeparture(): boolean {
    const isInternationalFlightDepartingFromJapan =
      this.flight.isNhGroupOperated &&
      this.flight.isJapanDomesticFlight === false &&
      this.flight.departure?.country2letterCode === 'JP';

    // 以下条件すべてを満たす場合、自宅等の出発地からまでの経路案内のリンクを表示する。
    const result =
      // 1) 当該セグメント情報.isOpenSegment=false(OPEN便でない)
      this.flight.isOpenSegment === false &&
      /** 2) 以下のいずれかを満たす
       * 2.1) 当該セグメント情報.isNhGroupOperated=true(NHグループ運航)、かつ
       *      当該セグメント情報.isJapanDomesticFlight=false(国際線)、かつ
       *      当該セグメント情報.departure.country2letterCode="JP"(日本発である)
       * 2.2) 当該セグメント情報.isJapanDomesticFlight=true(日本発国際線)
       */
      (isInternationalFlightDepartingFromJapan || (this.flight.isJapanDomesticFlight ?? false)) &&
      /** 3) 以下のいずれかを満たす
       * 3.1) バウンドの第一便である
       * 3.2) 当該セグメントのインデックス≠0、
       *      かつ当該バウンド.flights[当該セグメントのインデックス-1].isMultiAirportConnection = true
       *      (セグメントでマルチエアポート空港間の移動が発生する)
       */
      (this.isFirstFlight || (!this.isFirstFlight && this.isMultiAirportConnection(this.flightIdx - 1)));
    return result;
  }

  // (1.1.57) 到着地からの経路案内
  isRouteArrival(): boolean {
    const isInternationalFlightDepartingFromJapan =
      this.flight.isNhGroupOperated &&
      this.flight.isJapanDomesticFlight === false &&
      this.flight.arrival?.country2letterCode === 'JP';

    // 以下条件すべてを満たす場合、自宅等の出発地からまでの経路案内のリンクを表示する。
    const result =
      this.flight.isOpenSegment === false &&
      /** 1) 以下のいずれかを満たす
       * 1.1) 当該セグメント情報.isNhGroupOperated=true(NHグループ運航)、かつ
       *      当該セグメント情報.isJapanDomesticFlight=false(国際線)、かつ
       *      当該セグメント情報.arrival.country2letterCode="JP"(日本発である)
       * 2.2) 当該セグメント情報.isJapanDomesticFlight=true(日本発国際線)
       */
      (isInternationalFlightDepartingFromJapan || (this.flight.isJapanDomesticFlight ?? false)) &&
      /** 2) 以下のいずれかを満たす
       * 2.1) バウンドの最終便である
       * 2.2) 当該セグメント情報.isMultiAirportConnection=true(セグメントでマルチエアポート空港間の移動が発生する)
       */
      (this.isLastFlight || this.isMultiAirportConnection(this.flightIdx));
    return result;
  }

  isMultiAirportConnection(index: number) {
    return this.bound.flights?.at(index)?.isMultiAirportConnection ?? false;
  }

  // (1.1.18) 出発地までの経路案内 & (2)「出発地までの経路案内」リンク押下時
  clickGuidanceToDeparture(flightId?: string) {
    /** ※[出発地までの経路案内リンク押下]処理へ渡すとき、 当該バウンドを特定するため、
      入力パラメータとして当該バウンド.airBoundIdと当該セグメント情報.idと対象便種別を設定する。*/
    this._myBookingItineraryService.functionList['clickGuidanceToDeparture'](this.bound.airBoundId, flightId);
  }

  /** (1.1.57) 到着地からの経路案内 & (4)「到着地からの経路案内」リンク押下時 */
  clickGuidanceToDestination(flightId?: string) {
    /** ※[到着地からの経路案内リンク押下]処理へ渡すとき、当該バウンドを特定するため、
      入力パラメータとして当該バウンド.airBoundIdと当該セグメント情報.idと対象便種別をに設定する。*/
    this._myBookingItineraryService.functionList['clickGuidanceToDestination'](this.bound.airBoundId, flightId);
  }

  override reload(): void {
    // Add comment to passed SonarLint
  }

  override destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
